import React from 'react';
import styled from '@emotion/styled';
import {
  Button,
  Typography,
  ContentCard,
  TitleText,
  TextEditor,
  Spacing,
} from '@taleemabad/dsm/components';
import { IFluidLessonPlanDetail } from '@taleemabad/db';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { removeUrduCharacters } from '@taleemabad/dsm/utils';

const isContentEmpty = (content: any): boolean => {
  if (!content) return true;
  if (
    typeof content === 'object' &&
    !Array.isArray(content) &&
    Object.keys(content).length === 0
  ) {
    return true;
  }
  if (content?.type === 'doc' && Array.isArray(content.content)) {
    if (
      content.content.length === 1 &&
      content.content[0]?.type === 'paragraph' &&
      (!content.content[0].content || content.content[0].content.length === 0)
    ) {
      return true;
    }
  }
  if (
    Array.isArray(content) &&
    content.length === 1 &&
    content[0]?.type === 'paragraph' &&
    (!content[0].content || content[0].content.length === 0)
  ) {
    return true;
  }
  return false;
};

interface FluidLessonPlanPreparationProps {
  fluidLpDetail: IFluidLessonPlanDetail;
  chapterId: number;
  chapterIndex: number;
  startLessonPlan: () => void;
}

export const FluidLessonPlanPreparation: React.FC<
  FluidLessonPlanPreparationProps
> = ({ fluidLpDetail, chapterIndex, chapterId, startLessonPlan }) => {
  const bookInfo = fluidLpDetail.bookInfo.filter(
    (info) => info.bookChapter === chapterId,
  )[0];

  const renderSlo = () =>
    fluidLpDetail.lpSlo?.length > 0 && (
      <ContentCard
        iconName="import_contacts"
        title="Student Learning Objective"
      >
        {fluidLpDetail.lpSlo.map((sloItem) => (
          <Typography variant="textBodyLargeNeutral40" key={sloItem}>
            {sloItem}
          </Typography>
        ))}
      </ContentCard>
    );

  const renderSummary = () => {
    const summarySection = fluidLpDetail.sections?.find(
      (section) => section.sectionType?.toLowerCase() === 'summary',
    );
    if (!summarySection?.variants?.length) {
      return (
        fluidLpDetail.summary?.length > 0 && (
          <ContentCard iconName="list_alt" title="Lesson Summary">
            {fluidLpDetail.summary.map((summaryItem, index) => (
              <Typography variant="textBodyLargeNeutral40" key={index}>
                {summaryItem.statement}
              </Typography>
            ))}
          </ContentCard>
        )
      );
    }
    const nonEmptyVariants = summarySection.variants.filter(
      (variantWrapper) => {
        const content = variantWrapper.variant?.content;
        return content && !isContentEmpty(content);
      },
    );
    if (nonEmptyVariants.length === 0) {
      return null;
    }
    return (
      <ContentCard iconName="list_alt" title="Lesson Summary">
        {nonEmptyVariants.map((variantWrapper) => {
          const { variant } = variantWrapper;
          return (
            <Spacing key={variant.id} marginBottom="sm">
              <TextEditor
                content={variant.content}
                isRtl={variant.isRtl ?? false}
              />
            </Spacing>
          );
        })}
      </ContentCard>
    );
  };

  const renderResourcesNeeded = () =>
    fluidLpDetail.resourcesNeeded?.length > 0 && (
      <ContentCard iconName="file_present" title="Resources">
        {fluidLpDetail.resourcesNeeded.map((resource, index) => (
          <Typography variant="textBodyLargeNeutral40" key={index}>
            {resource}
          </Typography>
        ))}
      </ContentCard>
    );

  const renderClassroomSetupInstructions = () => {
    const classroomSetupSection = fluidLpDetail.sections?.find(
      (section) =>
        section.sectionType?.toLowerCase().includes('classroom') ||
        section.sectionType?.toLowerCase().includes('setup'),
    );

    if (!classroomSetupSection?.variants?.length) {
      return (
        fluidLpDetail.classroomSetupInstructions?.length > 0 && (
          <ContentCard iconName="list_alt" title="Classroom Setup">
            {fluidLpDetail.classroomSetupInstructions.map(
              (instruction, index) => (
                <Typography variant="textBodyLargeNeutral40" key={index}>
                  {instruction.statement}
                </Typography>
              ),
            )}
          </ContentCard>
        )
      );
    }

    const nonEmptyVariants = classroomSetupSection.variants.filter(
      (variantWrapper) => {
        const content = variantWrapper.variant?.content;
        return content && !isContentEmpty(content);
      },
    );
    if (nonEmptyVariants.length === 0) {
      return null;
    }

    return (
      <ContentCard iconName="list_alt" title="Classroom Setup">
        {nonEmptyVariants.map((variantWrapper) => {
          const { variant } = variantWrapper;
          return (
            <Spacing key={variant.id} marginBottom="sm">
              <TextEditor
                content={variant.content}
                isRtl={variant.isRtl ?? false}
              />
            </Spacing>
          );
        })}
      </ContentCard>
    );
  };

  const renderPreparationSections = () => {
    const preparationSections = fluidLpDetail.sections?.filter((section) => {
      const sectionType = section.sectionType?.toLowerCase();
      return (
        sectionType?.includes('preparation') ||
        sectionType?.includes('materials') ||
        sectionType?.includes('resources') ||
        sectionType?.includes('overview')
      );
    });

    if (!preparationSections?.length) {
      return null;
    }

    const sectionsWithContent = preparationSections.filter((section) => {
      const nonEmptyVariants = section.variants.filter((variantWrapper) => {
        const content = variantWrapper.variant?.content;
        return content && !isContentEmpty(content);
      });
      return nonEmptyVariants.length > 0;
    });

    if (sectionsWithContent.length === 0) {
      return null;
    }

    return sectionsWithContent.map((section) => {
      const nonEmptyVariants = section.variants.filter((variantWrapper) => {
        const content = variantWrapper.variant?.content;
        return content && !isContentEmpty(content);
      });

      return (
        <ContentCard
          key={section.id}
          iconName="info"
          title={section.sectionType}
        >
          {nonEmptyVariants.map((variantWrapper) => {
            const { variant } = variantWrapper;
            return (
              <Spacing key={variant.id} marginBottom="sm">
                <TextEditor
                  content={variant.content}
                  isRtl={variant.isRtl ?? false}
                />
              </Spacing>
            );
          })}
        </ContentCard>
      );
    });
  };

  return (
    <FluidLessonPlanPreparationContainer>
      <Typography variant="textHeadlineMediumNeutral40">
        {removeUrduCharacters(fluidLpDetail.title || fluidLpDetail.topic)}
      </Typography>

      <TitleTextContainer>
        <TitleText
          leftText={`Topic ${chapterIndex}`}
          seperatorIconName="circle"
          rightText={`Lesson ${bookInfo?.index}`}
        />
      </TitleTextContainer>

      <ContentCardContainer>
        {renderSlo()}
        {renderSummary()}
        {renderResourcesNeeded()}
        {renderClassroomSetupInstructions()}
        {renderPreparationSections()}
      </ContentCardContainer>

      <StyledButtonContainer>
        <Button variant="filled" onClick={startLessonPlan}>
          Go To Lesson
        </Button>
      </StyledButtonContainer>
    </FluidLessonPlanPreparationContainer>
  );
};

const FluidLessonPlanPreparationContainer = styled.div`
  overflow-y: overlay;
  height: calc(100vh - 100px);
  ${mq(BreakpointSizes.xs)} {
    height: calc(100vh - 80px);
  }
`;

const TitleTextContainer = styled.div`
  margin: var(--rk-spacing-md) 0 var(--rk-spacing-xl) 0;
`;

const ContentCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: var(--rk-spacing-xl);
  padding-bottom: var(--rk-spacing-xxxxxxxxl);
`;

const StyledButtonContainer = styled.div`
  position: absolute;
  bottom: 50px;
  right: var(--rk-spacing-xxxxxl);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  ${mq(BreakpointSizes.xs)} {
    right: var(--rk-spacing-xl);
    width: calc(100% - var(--rk-spacing-xl));
    bottom: 0;
    height: 75px;
    background-color: var(--md-sys-color-on-primary);
  }
`;
